import React, { useEffect, useState } from "react";
import { Plus, Wand2, Trash2, Eye, LayoutGrid, List as ListIcon, Search } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";

export default function AdminBlogs() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [view, setView] = useState("grid");

  const fetchBlogs = async () => {
    try {
      const res = await api.get("/manage/blogs");
      setBlogs(res.data);
    } catch (err) {
      toast.error("Failed to fetch blogs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    toast.promise(api.post("/manage/generate-blogs"), {
      loading: "AI is crafting your blog posts...",
      success: (res) => {
        fetchBlogs();
        return res.data.message || "Blogs generated successfully!";
      },
      error: "Failed to generate blogs. Check API keys.",
      finally: () => setGenerating(false)
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this blog?")) return;
    try {
      await api.delete(`/manage/blogs/${id}`);
      setBlogs(blogs.filter(b => b.id !== id));
      toast.success("Blog deleted");
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-serif-display text-slate-900">Blog Management</h1>
          <p className="text-slate-500 mt-1">Manage your AI-powered blog content</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-full font-bold shadow-lg shadow-purple-200 hover:shadow-xl hover:scale-105 transition-all disabled:opacity-50 disabled:scale-100"
          >
            <Wand2 className={`w-4 h-4 ${generating ? "animate-spin" : ""}`} />
            {generating ? "Generating..." : "AI Generate Blogs"}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-slate-100">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4 bg-slate-50 p-1.5 rounded-2xl">
            <button
              onClick={() => setView("grid")}
              className={`p-2 rounded-xl transition ${view === "grid" ? "bg-white shadow-sm text-primary" : "text-slate-400"}`}
            >
              <LayoutGrid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setView("list")}
              className={`p-2 rounded-xl transition ${view === "list" ? "bg-white shadow-sm text-primary" : "text-slate-400"}`}
            >
              <ListIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-slate-400 animate-pulse font-medium">Fetching blog list...</p>
          </div>
        ) : blogs.length === 0 ? (
          <div className="text-center py-20 border-2 border-dashed border-slate-100 rounded-[2rem]">
            <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-slate-300" />
            </div>
            <h3 className="text-xl font-bold text-slate-800">No Blogs Yet</h3>
            <p className="text-slate-500 mt-2 max-w-sm mx-auto">
              Use the AI Generate button to automatically create SEO-optimized blogs based on your packages.
            </p>
          </div>
        ) : (
          <div className={view === "grid" ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" : "space-y-4"}>
            {blogs.map((blog) => (
              <motion.div
                layout
                key={blog.id}
                className={`group bg-white border border-slate-100 rounded-[2rem] overflow-hidden hover:shadow-xl hover:border-primary/20 transition-all duration-500 ${view === "list" ? "flex items-center gap-6 p-4" : ""}`}
              >
                <div className={`relative overflow-hidden ${view === "list" ? "w-40 h-24 rounded-2xl" : "h-48"}`}>
                  <img
                    src={blog.image || "https://images.unsplash.com/photo-1513151233558-d860c5398176?w=800"}
                    alt={blog.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  {blog.active && (
                    <div className="absolute top-3 left-3 px-3 py-1 bg-green-500 text-white text-[10px] font-black uppercase rounded-full shadow-lg">
                      Published
                    </div>
                  )}
                </div>
                <div className={`p-6 ${view === "list" ? "flex-1 p-0" : ""}`}>
                  <h3 className="font-bold text-slate-800 line-clamp-2 leading-tight h-10 mb-2">
                    {blog.title}
                  </h3>
                  <div className="text-xs text-slate-400 mb-4 flex items-center gap-2">
                    {new Date(blog.created_at).toLocaleDateString()} • {blog.slug}
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <a
                      href={`/blog/${blog.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="p-2 text-slate-400 hover:text-primary hover:bg-primary/5 rounded-xl transition"
                    >
                      <Eye className="w-5 h-5" />
                    </a>
                    <button
                      onClick={() => handleDelete(blog.id)}
                      className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
